import React from "react";
import { Input, Slider, Tooltip } from "antd";
import { map } from "lodash";
import { AICC_SANDBOX_FLUENCY } from "constants/aiccPromptSandbox";
import { AiccPromptVariable } from "types/model/aiccPrompt";
import { AiccSandboxInputs } from "types/model/aiccPromptSandbox";
import { FLUENCY_VARIABLE, SandboxVariableField, sandboxVariableFields } from "./variableInputs";
import { FLUENCY_HINT, INPUTS_DESCRIPTION, INPUTS_NO_VARIABLES, REQUIRED_MARK } from "../configs";
import styles from "../Sandbox.module.scss";

interface Props {
  variables: AiccPromptVariable[];
  inputs: AiccSandboxInputs;
  fixedNames: string[];
  isDisabled?: boolean;
  onChange: (inputs: AiccSandboxInputs) => void;
}

function VariableInputsForm({ variables, inputs, fixedNames, isDisabled, onChange }: Props) {
  const fluency = Number(inputs[FLUENCY_VARIABLE]) || AICC_SANDBOX_FLUENCY.default;
  const fields = sandboxVariableFields(variables);

  const setValue = (name: string, value: string) => onChange({ ...inputs, [name]: value });

  const label = ({ name, description, isRequired }: SandboxVariableField) => (
    <Tooltip title={description}>
      <span className={styles.fieldLabel}>
        {name}
        {isRequired && <span className={styles.fieldRequired}>{REQUIRED_MARK}</span>}
      </span>
    </Tooltip>
  );

  const fluencyField = (field: SandboxVariableField) => (
    <label key={field.name} className={styles.fieldWide}>
      <span className={styles.fieldLabel}>
        {field.name}
        <span className={styles.fluencyValue}>{fluency}</span>
        <span className={styles.fluencyHint}>{FLUENCY_HINT}</span>
      </span>
      <div className={styles.fluencySlider}>
        <Slider
          min={AICC_SANDBOX_FLUENCY.min}
          max={AICC_SANDBOX_FLUENCY.max}
          step={1}
          value={fluency}
          disabled={isDisabled}
          onChange={(value: number) => setValue(FLUENCY_VARIABLE, String(value))}
        />
      </div>
    </label>
  );

  const variableField = (field: SandboxVariableField) => {
    const { name, isLong, isFluency } = field;

    if (isFluency) {
      return fluencyField(field);
    }

    const isReadOnly = isDisabled || fixedNames.includes(name);

    return (
      <label key={name} className={isLong ? styles.fieldWide : styles.field}>
        {label(field)}
        {isLong ? (
          <Input.TextArea
            rows={2}
            value={inputs[name]}
            disabled={isReadOnly}
            onChange={(event) => setValue(name, event.target.value)}
          />
        ) : (
          <Input value={inputs[name]} disabled={isReadOnly} onChange={(event) => setValue(name, event.target.value)} />
        )}
      </label>
    );
  };

  return (
    <div className={styles.inputs}>
      <div className={styles.inputsDescription}>{INPUTS_DESCRIPTION}</div>

      <div className={styles.fields}>
        {map(fields, variableField)}

        {!fields.length && <div className={styles.inputsDescription}>{INPUTS_NO_VARIABLES}</div>}
      </div>
    </div>
  );
}

export default VariableInputsForm;
